import React, { useState, useEffect } from 'react';
import { useQRScans } from '../../../authentication_components/businessScansScontext';
import '../styles/EditScansModal.css';

/**
 * EditScanModal Component
 * 
 * Modal dialog for editing QR scan records
 * Allows updating scan fields like country, city, device type, etc.
 */
export function EditScanModal({ scan, isOpen, onClose, onSuccess }) {
  // ========================
  // STATE
  // ========================

  const [formData, setFormData] = useState({
    country: '',
    city: '',
    device_type: '',
    browser_name: '',
    os_name: '',
    ip_address: '',
    user_agent: '',
    referrer: '',
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const { updateScan, loading, error, clearError } = useQRScans();

  // ========================
  // EFFECTS
  // ========================

  useEffect(() => {
    if (scan) {
      setFormData({
        country: scan.country || '',
        city: scan.city || '',
        device_type: scan.device_type || '',
        browser_name: scan.browser_name || '',
        os_name: scan.os_name || '',
        ip_address: scan.ip_address || '',
        user_agent: scan.user_agent || '',
        referrer: scan.referrer || '',
      });
      setErrors({});
      clearError();
    }
  }, [scan, isOpen, clearError]);

  // ========================
  // HANDLERS
  // ========================

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate country (only if provided)
    if (formData.country && formData.country.length > 50) {
      newErrors.country = 'Country must be 50 characters or less';
    }

    // Validate city (only if provided)
    if (formData.city && formData.city.length > 100) {
      newErrors.city = 'City must be 100 characters or less';
    }

    // Validate device type
    const validDeviceTypes = ['', 'desktop', 'mobile', 'tablet'];
    if (formData.device_type && !validDeviceTypes.includes(formData.device_type)) {
      newErrors.device_type = 'Invalid device type';
    }

    // Validate browser name (only if provided)
    if (formData.browser_name && formData.browser_name.length > 50) {
      newErrors.browser_name = 'Browser name must be 50 characters or less';
    }

    // Validate OS name (only if provided)
    if (formData.os_name && formData.os_name.length > 50) {
      newErrors.os_name = 'OS name must be 50 characters or less';
    }

    // Validate IP address format (basic check)
    if (formData.ip_address && formData.ip_address.length > 0) {
      const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
      if (!ipRegex.test(formData.ip_address) && formData.ip_address.length > 0) {
        newErrors.ip_address = 'Invalid IP address format (e.g., 192.168.1.1)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      // Only send non-empty fields to API
      const updateData = Object.fromEntries(
        Object.entries(formData).filter(([, value]) => value !== '')
      );

      await updateScan(scan.scan_id, updateData);

      // Success - close modal and notify parent
      onSuccess?.({
        ...scan,
        ...updateData,
      });

      handleClose();
    } catch (err) {
      console.error('Error updating scan:', err);
      // Error is handled by context
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      country: '',
      city: '',
      device_type: '',
      browser_name: '',
      os_name: '',
      ip_address: '',
      user_agent: '',
      referrer: '',
    });
    setErrors({});
    clearError();
    onClose();
  };

  if (!isOpen || !scan) {
    return null;
  }

  // ========================
  // RENDER
  // ========================

  return (
    <div className="edit-scan-modal-overlay">
      <div className="edit-scan-modal">
        <div className="edit-scan-modal-header">
          <h2>Edit Scan #{scan.scan_id}</h2>
          <button
            type="button"
            onClick={handleClose}
            className="edit-scan-close-btn"
            disabled={isSaving || loading}
          >
            ✕
          </button>
        </div>

        <div className="edit-scan-modal-content">
          {/* Error Message */}
          {error && (
            <div className="edit-scan-error-alert">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
              <button
                type="button"
                onClick={clearError}
                className="error-close"
              >
                ✕
              </button>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="edit-scan-form">
            {/* Scan Info Display */}
            <div className="scan-info-display">
              <div className="info-row">
                <span className="info-label">Scan ID:</span>
                <span className="info-value">#{scan.scan_id}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Scanned At:</span>
                <span className="info-value">
                  {new Date(scan.scanned_at).toLocaleString()}
                </span>
              </div>
              {scan.qr_name && (
                <div className="info-row">
                  <span className="info-label">QR Name:</span>
                  <span className="info-value">{scan.qr_name}</span>
                </div>
              )}
            </div>

            {/* Form Fields Grid */}
            <div className="edit-scan-form-grid">
              {/* Country */}
              <div className="edit-scan-form-group">
                <label htmlFor="country">Country</label>
                <input
                  id="country"
                  type="text"
                  name="country"
                  placeholder="e.g., United States"
                  value={formData.country}
                  onChange={handleInputChange}
                  disabled={isSaving || loading}
                  className={errors.country ? 'has-error' : ''}
                />
                {errors.country && (
                  <span className="field-error">{errors.country}</span>
                )}
              </div>

              {/* City */}
              <div className="edit-scan-form-group">
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  type="text"
                  name="city"
                  placeholder="e.g., New York"
                  value={formData.city}
                  onChange={handleInputChange}
                  disabled={isSaving || loading}
                  className={errors.city ? 'has-error' : ''}
                />
                {errors.city && (
                  <span className="field-error">{errors.city}</span>
                )}
              </div>

              {/* Device Type */}
              <div className="edit-scan-form-group">
                <label htmlFor="device_type">Device Type</label>
                <select
                  id="device_type"
                  name="device_type"
                  value={formData.device_type}
                  onChange={handleInputChange}
                  disabled={isSaving || loading}
                  className={errors.device_type ? 'has-error' : ''}
                >
                  <option value="">Select device type</option>
                  <option value="desktop">Desktop</option>
                  <option value="mobile">Mobile</option>
                  <option value="tablet">Tablet</option>
                </select>
                {errors.device_type && (
                  <span className="field-error">{errors.device_type}</span>
                )}
              </div>

              {/* Browser */}
              <div className="edit-scan-form-group">
                <label htmlFor="browser_name">Browser</label>
                <input
                  id="browser_name"
                  type="text"
                  name="browser_name"
                  placeholder="e.g., Chrome"
                  value={formData.browser_name}
                  onChange={handleInputChange}
                  disabled={isSaving || loading}
                  className={errors.browser_name ? 'has-error' : ''}
                />
                {errors.browser_name && (
                  <span className="field-error">{errors.browser_name}</span>
                )}
              </div>

              {/* OS */}
              <div className="edit-scan-form-group">
                <label htmlFor="os_name">Operating System</label>
                <input
                  id="os_name"
                  type="text"
                  name="os_name"
                  placeholder="e.g., Windows"
                  value={formData.os_name}
                  onChange={handleInputChange}
                  disabled={isSaving || loading}
                  className={errors.os_name ? 'has-error' : ''}
                />
                {errors.os_name && (
                  <span className="field-error">{errors.os_name}</span>
                )}
              </div>

              {/* IP Address */}
              <div className="edit-scan-form-group">
                <label htmlFor="ip_address">IP Address</label>
                <input
                  id="ip_address"
                  type="text"
                  name="ip_address"
                  placeholder="e.g., 192.168.1.1"
                  value={formData.ip_address}
                  onChange={handleInputChange}
                  disabled={isSaving || loading}
                  className={errors.ip_address ? 'has-error' : ''}
                />
                {errors.ip_address && (
                  <span className="field-error">{errors.ip_address}</span>
                )}
              </div>
            </div>

            {/* User Agent */}
            <div className="edit-scan-form-group full-width">
              <label htmlFor="user_agent">User Agent</label>
              <textarea
                id="user_agent"
                name="user_agent"
                placeholder="Browser user agent string"
                value={formData.user_agent}
                onChange={handleInputChange}
                disabled={isSaving || loading}
                rows="2"
              />
            </div>

            {/* Referrer */}
            <div className="edit-scan-form-group full-width">
              <label htmlFor="referrer">Referrer</label>
              <input
                id="referrer"
                type="text"
                name="referrer"
                placeholder="HTTP referrer URL"
                value={formData.referrer}
                onChange={handleInputChange}
                disabled={isSaving || loading}
              />
            </div>

            {/* Buttons */}
            <div className="edit-scan-form-buttons">
              <button
                type="button"
                onClick={handleClose}
                className="edit-scan-cancel-btn"
                disabled={isSaving || loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="edit-scan-save-btn"
                disabled={isSaving || loading}
              >
                {isSaving || loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditScanModal;